/**
 * Feature Flags Management Page — flag-centric master-detail layout.
 */

import { useEffect, useMemo, useState } from 'react';
import { Plus, AlertCircle, Flag } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  FlagList,
  FlagDetail,
  FlagEditor,
  RetirementPanel,
  useFeatureFlags,
  useCreateFlag,
} from '@/features/feature-flags';
import { useI18n } from '@/i18n';
import type { CreateFlagRequest } from '@/types/featureFlags';

export function FeatureFlagsPage() {
  const { t } = useI18n();
  const [selectedFlagKey, setSelectedFlagKey] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isRetirementOpen, setIsRetirementOpen] = useState(false);

  const { data, isLoading, error } = useFeatureFlags();
  const createMutation = useCreateFlag();

  const flags = useMemo(() => data?.flags ?? [], [data?.flags]);
  const selectedFlag = flags.find((f) => f.flag_key === selectedFlagKey) ?? null;

  // Initial selection only. We deliberately do NOT reset a set-but-missing key:
  // right after create, selectedFlagKey points at the new flag before its refetch
  // lands (it would otherwise snap back to flags[0]). Delete clears the selection
  // explicitly (onDeleted -> null), which re-triggers this effect to pick flags[0].
  useEffect(() => {
    if (flags.length === 0) {
      if (selectedFlagKey !== null) setSelectedFlagKey(null);
      return;
    }
    if (selectedFlagKey === null) {
      setSelectedFlagKey(flags[0].flag_key);
    }
  }, [flags, selectedFlagKey]);

  const handleCreate = async (flagData: CreateFlagRequest) => {
    try {
      await createMutation.mutateAsync(flagData);
      setIsEditorOpen(false);
      setSelectedFlagKey(flagData.flag_key);
    } catch {
      // toast already shown by mutation onError; keep dialog open
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Flag className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('featureFlags.pageTitle')}</h1>
            <p className="text-gray-600 text-sm">{t('featureFlags.pageDescription')}</p>
          </div>
        </div>
        <Button variant="primary" onClick={() => setIsEditorOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('featureFlags.createFlag')}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-semibold">{t('featureFlags.errors.loadingError')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Master-detail */}
      {!isLoading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-6 items-start">
          {/* Master */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-[calc(100vh-12rem)]">
            <FlagList
              flags={flags}
              selectedFlagKey={selectedFlagKey}
              onSelect={setSelectedFlagKey}
              onOpenRetirement={() => setIsRetirementOpen(true)}
            />
          </div>

          {/* Detail */}
          <div className="bg-white rounded-lg border border-gray-200 min-h-[calc(100vh-12rem)]">
            {selectedFlag ? (
              <FlagDetail flag={selectedFlag} onDeleted={() => setSelectedFlagKey(null)} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-24 text-center text-gray-400">
                <Flag className="w-12 h-12 mb-3 opacity-40" />
                <p className="text-base font-medium text-gray-500">{t('featureFlags.detail.emptyTitle')}</p>
                <p className="text-sm mt-1">{t('featureFlags.detail.emptyHint')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Dialog */}
      {isEditorOpen && (
        <FlagEditor
          existingKeys={flags.map((f) => f.flag_key)}
          onSave={handleCreate}
          onClose={() => setIsEditorOpen(false)}
          isSubmitting={createMutation.isPending}
        />
      )}

      {/* Retirement Panel */}
      {isRetirementOpen && (
        <RetirementPanel flags={flags} onClose={() => setIsRetirementOpen(false)} />
      )}
    </div>
  );
}
